import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import {
  CreateVisualQualityControlDto,
  VisualQualityControlDto,
  VisualQualityControlType,
} from '@services/dtos'
import { visualQualityControlSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Grid } from '@mui/material'
import {
  createVisualQualityControl,
  updateVisualQualityControl,
} from '@state/reducers/visualQualityControlReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import InputField from '../inputs/InputField'
import SelectField from '../inputs/SelectField'
import { VisualQualityControlTypeLabels } from '@utils/constants'
import { FormCrudHeader } from './FormCrudHeader'

const VISUAL_QUALITY_CONTROL_TYPE_OPTIONS = (
  Object.entries(VisualQualityControlTypeLabels) as Array<
    [VisualQualityControlType, string]
  >
).map(([value, label]) => ({ label, value }))

type Props = {
  editingVisualQualityControl?: VisualQualityControlDto | null
  onClose: () => void
  defaultValues?: Partial<CreateVisualQualityControlDto>
}

const VisualQualityControlForm: React.FC<Props> = ({
  editingVisualQualityControl,
  onClose,
  defaultValues
}) => {
  const { watch, handleSubmit, reset, control } =
    useForm<CreateVisualQualityControlDto>({
      defaultValues: editingVisualQualityControl
        ? {
            type: editingVisualQualityControl.type,
            label: editingVisualQualityControl.label,
          }
        : defaultValues,
      resolver: yupResolver(visualQualityControlSchema),
    })

  const dispatch = useAppDispatch()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      reset()
    }
  }, [dispatch])

  const onSubmitForm = async (data: CreateVisualQualityControlDto) => {
    setIsSubmitting(true)

    if (editingVisualQualityControl) {
      dispatch(
        updateVisualQualityControl({
          id: editingVisualQualityControl.id,
          dto: data,
        }),
      )
        .unwrap()
        .then(() => {
          cb('Le contrôle qualité visuel a été modifié avec succès')
          onClose()
        })
        .catch(() =>
          cb(
            'Une erreur est survenue, vérifiez que le libellé n\'est pas déjà utilisé',
          ),
        )
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createVisualQualityControl(data))
        .unwrap()
        .then(() => {
          cb('Le contrôle qualité visuel a été créé avec succès')
          onClose()
        })
        .catch(() =>
          cb(
            'Une erreur est survenue, vérifiez que le libellé n\'est pas déjà utilisé',
          ),
        )
        .finally(() => setIsSubmitting(false))
    }
  }

  return (
    <div className="flex w-full h-full p-4">
      <form         
        onSubmit={(e) => {
          e.stopPropagation()
          handleSubmit(onSubmitForm)(e)
        }} 
        className="w-full">
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingVisualQualityControl}
        />

        <div className="max-w-screen-sm mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Contrôle qualité visuel
            </legend>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              <Grid item xs={12}>
                <SelectField
                  control={control}
                  name={'type'}
                  options={VISUAL_QUALITY_CONTROL_TYPE_OPTIONS}
                  value={watch('type')}
                  label={'Type'}
                />
              </Grid>

              <Grid item xs={12}>
                <InputField
                  control={control}
                  name={'label'}
                  label={'Libellé'}
                />
              </Grid>
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default VisualQualityControlForm
